import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Menu, Upload, Sun, Moon, MoreVertical } from 'lucide-react';
import { RootState } from '../../store/store';
import { setPhotos, setPhotosLoading, setPhotoError, addPhotos, appendPhotos } from '../../slice/photosSlice';
import { setTags } from '../../slice/tagsSlice';
import { photoService, tagService, authService } from '../../services';
import { authUserSignal } from '../../signals/authSignal';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { createLogger } from '../../utils/logger';
import { usePhotoNavigation } from '../../hooks/usePhotoNavigation';
import Sidebar from './dashboard/Sidebar';
import GlassDock from './dashboard/GlassDock';
import FooterBar from './dashboard/FooterBar';
import PhotoDetailModal from './dashboard/PhotoDetailModal';
import PhotoThumbnailItem from './dashboard/PhotoThumbnailItem';
import SettingsPanel from './dashboard/SettingsPanel';
import UploadModal from './dashboard/UploadModal';

const logger = createLogger('DashboardPage');

export default function DashboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme, toggleTheme, themeSelection } = useTheme();
  const { t } = useLanguage();

  // Redux selectors
  const { byId: photosById, allIds: photoIds, loading } = useSelector((state: RootState) => state.photos);
  const filters = useSelector((state: RootState) => state.filters);
  const tags = useSelector((state: RootState) => state.tags);

  // Local state for UI interactions
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Photo modal state from URL params
  const photoIdParam = searchParams.get('photo');
  const selectedPhotoId = photoIdParam ? parseInt(photoIdParam, 10) : null;

  // Infinite scroll pagination state
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const gridAreaRef = useRef<HTMLDivElement>(null);

  // Get current user from signal
  const currentUser = authUserSignal.value;

  // Page size from env var (VITE_PHOTOS_PAGE_SIZE), default 50
  const pageSize = parseInt(import.meta.env.VITE_PHOTOS_PAGE_SIZE || '50', 10);

  // Load tags once on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tagsData = await tagService.getAllTags();
        dispatch(setTags(tagsData));
      } catch (err) {
        logger.warn('Failed to load tags:', err);
      }
    };
    loadTags();
  }, [dispatch]);

  // Fetch photos with current filters
  const fetchPhotos = useCallback(async () => {
    try {
      dispatch(setPhotosLoading(true));
      
       logger.debug('Fetching photos with filters:', { people: filters.people, places: filters.places, years: filters.years, events: filters.events, visibility: filters.visibility });
      
      // Translate selected tag names to IDs
      const selectedTagIds: number[] = [];
      
      // Add People tag IDs
      filters.people.forEach(personName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'person' && t.name === personName
        );
        if (tag) {
          logger.debug('Found person tag:', tag);
          selectedTagIds.push(tag.id);
        }
      });
      
      // Add Places tag IDs
      filters.places.forEach(placeName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'place' && t.name === placeName
        );
        if (tag) {
          logger.debug('Found place tag:', tag);
          selectedTagIds.push(tag.id);
        }
      });
      
      // Add Years tag IDs (if backend supports year as tag filter)
      filters.years.forEach(yearName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'year' && t.name === yearName
        );
        if (tag) {
          logger.debug('Found year tag:', tag);
          selectedTagIds.push(tag.id);
        }
      });

      // Add Events tag IDs
      filters.events.forEach(eventName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'event' && t.name === eventName
        );
        if (tag) {
          logger.debug('Found event tag:', tag);
          selectedTagIds.push(tag.id);
        }
      });

      // Use comma-separated tags for multi-tag filtering
      const tagsParam = selectedTagIds.length > 0 ? selectedTagIds.join(',') : undefined;
      
      // Determine visibility param
      const visibility = filters.visibility === 'all' ? undefined : filters.visibility;
      
      logger.debug('API call params:', { tags: tagsParam, visibility });
      
      // Fetch photos with filters (using new tags parameter)
      const photosData = await photoService.getPhotos(50, 0, {
        tags: tagsParam,
        visibility: visibility as 'public' | 'private' | undefined,
      });
      
      logger.debug('Received photos:', photosData.length);
      
      dispatch(setPhotos(photosData));
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load photos';
      logger.error('Error fetching photos:', err);
      setError(message);
      dispatch(setPhotoError(message));
    } finally {
      dispatch(setPhotosLoading(false));
    }
  }, [dispatch, filters, tags.byId]);

  // Re-fetch photos when filters change (after tags are loaded).
  // NOTE: fetchPhotos is intentionally NOT a dependency here. It is recreated
  // whenever tags.byId gets a new reference (e.g. opening the photo modal's
  // Tags tab dispatches setPhotoTags, which replaces tags.byId even if the
  // tag data is unchanged). If fetchPhotos were a dependency, that unrelated
  // identity change would re-run this effect, reset pagination to page 1, and
  // drop any photos loaded via infinite scroll - closing the modal if the
  // open photo was one of them.
  const fetchPhotosRef = useRef(fetchPhotos);
  fetchPhotosRef.current = fetchPhotos;

  useEffect(() => {
    // Wait for tags to load before fetching photos
    if (tags.allIds.length === 0) return;

    // Reset pagination when filters change
    setOffset(0);
    setHasMore(true);
    fetchPhotosRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tags.allIds.length]);

  // Fetch next batch of photos on scroll
  const fetchMorePhotos = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    try {
      setLoadingMore(true);
      const newOffset = offset + pageSize;

      // Translate selected tag names to IDs (same logic as fetchPhotos)
      const selectedTagIds: number[] = [];
      
      filters.people.forEach(personName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'person' && t.name === personName
        );
        if (tag) selectedTagIds.push(tag.id);
      });
      
      filters.places.forEach(placeName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'place' && t.name === placeName
        );
        if (tag) selectedTagIds.push(tag.id);
      });
      
      filters.years.forEach(yearName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'year' && t.name === yearName
        );
        if (tag) selectedTagIds.push(tag.id);
      });

      filters.events.forEach(eventName => {
        const tag = Object.values(tags.byId).find(
          t => t.type === 'event' && t.name === eventName
        );
        if (tag) selectedTagIds.push(tag.id);
      });

      const tagsParam = selectedTagIds.length > 0 ? selectedTagIds.join(',') : undefined;
      const visibility = filters.visibility === 'all' ? undefined : filters.visibility;

      logger.debug(`Fetching more photos: offset=${newOffset}, pageSize=${pageSize}`);

      const morePhotos = await photoService.getPhotos(pageSize, newOffset, {
        tags: tagsParam,
        visibility: visibility as 'public' | 'private' | undefined,
      });

      if (morePhotos.length < pageSize) {
        setHasMore(false);
      }

      dispatch(appendPhotos(morePhotos));
      setOffset(newOffset);
      logger.debug(`Loaded ${morePhotos.length} more photos`);
    } catch (err) {
      logger.error('Error fetching more photos:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [offset, hasMore, loadingMore, pageSize, filters, tags.byId, dispatch]);

  // Scroll handler for infinite scroll
  useEffect(() => {
    const gridArea = gridAreaRef.current;
    if (!gridArea) return;

    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = gridArea;
      const scrollThreshold = 300; // pixels from bottom

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        logger.debug('Near bottom of scroll, fetching more photos');
        fetchMorePhotos();
      }
    };

    gridArea.addEventListener('scroll', handleScroll);
    return () => gridArea.removeEventListener('scroll', handleScroll);
  }, [fetchMorePhotos]);

  // Photos are already filtered by the API (by tag and visibility)
  // Just map them to an array for display
  const filteredPhotos = photoIds.map(id => photosById[id]);
  const selectedPhoto = selectedPhotoId ? photosById[selectedPhotoId] : null;

  // Photo navigation
  const navigation = usePhotoNavigation({
    filteredPhotos,
    currentPhotoId: selectedPhotoId,
  });

  const handlePhotoClick = (photoId: number) => {
    setSearchParams({ photo: photoId.toString() });
  };

  const closePhotoDetail = () => {
    setSearchParams({});
  };

  const handleNavigateToPrevious = useCallback(() => {
    if (navigation.previousPhotoId) {
      setSearchParams({ photo: navigation.previousPhotoId.toString() });
      logger.debug(`Navigating to previous photo: ${navigation.previousPhotoId}`);
    }
  }, [navigation.previousPhotoId, setSearchParams]);

  const handleNavigateToNext = useCallback(() => {
    if (navigation.nextPhotoId) {
      setSearchParams({ photo: navigation.nextPhotoId.toString() });
      logger.debug(`Navigating to next photo: ${navigation.nextPhotoId}`);
    }
  }, [navigation.nextPhotoId, setSearchParams]);

  const handleLogout = async () => {
    // Call backend to revoke token family
    await authService.logout();
    // Clear auth state and navigate to login
    authUserSignal.value = undefined;
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  const handleUploadComplete = (newPhotos: any[]) => {
    dispatch(addPhotos(newPhotos));
    setUploadOpen(false);
  };

   return (
     <div className="app" data-theme={currentTheme}>
       {/* Glass header */}
       <header className="glass-header">
         <div className="header-left">
            <button
              className="header-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={t('header.toggleFilters')}
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
            <h1 className="app-title">{t('header.title')}</h1>
         </div>
          <div className="header-right">
            {/* Desktop actions */}
            <div className="desktop-actions">
                <button
                  className="header-btn"
                  onClick={() => setUploadOpen(true)}
                  title={t('header.upload')}
                >
                  <Upload size={20} strokeWidth={1.5} />
                </button>
                <button
                  className="header-btn"
                  onClick={toggleTheme}
                  title={themeSelection.currentMode === 'dark' ? t('header.lightMode') : t('header.darkMode')}
                >
                  {themeSelection.currentMode === 'dark' ? (
                    <Sun size={20} strokeWidth={1.5} />
                  ) : (
                    <Moon size={20} strokeWidth={1.5} />
                  )}
                </button>
            </div>
            
            {/* Mobile menu button */}
            <div className="mobile-menu-btn" style={{ position: 'relative' }}>
              <button
                className="header-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                title={t('header.moreOptions')}
              >
                <MoreVertical size={20} strokeWidth={1.5} />
              </button>
                {mobileMenuOpen && (
                 <div className="mobile-menu-dropdown">
                   <div className="mobile-menu-user-info">
                     {currentUser?.name || t('dashboard.user')} {currentUser?.role && `(${currentUser.role})`}
                   </div>
                   <button
                     className="mobile-menu-item"
                     onClick={() => {
                       setUploadOpen(true);
                       setMobileMenuOpen(false);
                     }}
                   >
                     <Upload size={18} strokeWidth={1.5} />
                     {t('header.upload')}
                   </button>
                   <button
                     className="mobile-menu-item"
                     onClick={() => {
                       toggleTheme();
                       setMobileMenuOpen(false);
                     }}
                   >
                     {themeSelection.currentMode === 'dark' ? (
                       <>
                         <Sun size={18} strokeWidth={1.5} />
                         {t('dashboard.toggleThemeLight')}
                       </>
                     ) : (
                       <>
                         <Moon size={18} strokeWidth={1.5} />
                         {t('dashboard.toggleThemeDark')}
                       </>
                     )}
                   </button>
                   <button
                     className="mobile-menu-item"
                     onClick={() => {
                       setSettingsOpen(true);
                       setMobileMenuOpen(false);
                     }}
                   >
                     <Menu size={18} strokeWidth={1.5} />
                     {t('header.settings')}
                   </button>
                   <button
                     className="mobile-menu-item danger"
                     onClick={() => {
                       handleLogout();
                       setMobileMenuOpen(false);
                     }}
                   >
                     {t('header.logout')}
                   </button>
                 </div>
               )}
            </div>
            
            {/* Desktop user badge and menu */}
            <div style={{ position: 'relative' }}>
               <div
                 className="user-badge"
                 onClick={() => setUserMenuOpen(!userMenuOpen)}
                 title="User menu"
               >
                 <span>{currentUser?.name || t('dashboard.user')}</span>
                {currentUser?.role && (
                  <span className="role-pill">{currentUser.role.toUpperCase()}</span>
                )}
              </div>
              {userMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'var(--surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    minWidth: '150px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <button
                    onClick={() => {
                      setSettingsOpen(true);
                      setUserMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t('header.settings')}
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setUserMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: '#d32f2f',
                    }}
                  >
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
       </header>

       {/* Sidebar overlay */}
       {sidebarOpen && (
         <div
           className="sidebar-overlay active"
           onClick={() => setSidebarOpen(false)}
         />
       )}

       {/* Sidebar filters - moved outside main-container to escape stacking context trap */}
       <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

       {/* Main container */}
       <div className="main-container">
         {/* Grid area */}
         <div className="grid-area" ref={gridAreaRef}>
           {error && <div className="error">{error}</div>}

          {loading ? (
            <div className="loading">{t('dashboard.loadingMemories')}</div>
          ) : filteredPhotos.length === 0 ? (
            <div className="loading">
              {photoIds.length === 0
                ? t('dashboard.noPhotos')
                : t('dashboard.noPhotosFiltered')}
            </div>
          ) : (
            <div className="mosaic-grid">
              {filteredPhotos.map((photo) => (
                <PhotoThumbnailItem
                  key={photo.id}
                  photoId={photo.id}
                  description={photo.description}
                  onClick={() => handlePhotoClick(photo.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Glass dock - floating filter bar (desktop) */}
      <GlassDock />

      {/* Footer bar - mobile filter bar */}
      <FooterBar />

      {/* Modals */}
      {selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={closePhotoDetail}
          onNavigatePrevious={handleNavigateToPrevious}
          onNavigateNext={handleNavigateToNext}
          canNavigatePrevious={navigation.canNavigatePrevious}
          canNavigateNext={navigation.canNavigateNext}
        />
      )}

      {settingsOpen && (
        <SettingsPanel onClose={() => setSettingsOpen(false)} />
      )}

      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </div>
  );
}
